import { useCallback, useEffect } from 'react';

import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useResumeEffect } from '@/hooks/use-resume';
import { useUserStore } from '@/hooks/use-user-store';
import { useBonusEngineClient, userPromosKeys } from '@/providers';
import { logger } from '@/utils/logging';
import { useMutation, useQuery } from '@tanstack/react-query';

import { DepositMatchPromo } from '../types';
import { usePromotion } from './use-promo-details';
import { usePromotionsStore } from './use-promotions-store';

/**
 * Fetches the deposit match promos available for the user per product.
 */
export const useDepositMatchPromos = (product: Product, { enabled }: { enabled?: boolean }) => {
    const bonusEngineAPIClient = useBonusEngineClient();
    const userId = useUserStore(state => state.profile.sub);
    const endpoint = `/submissions/user/${userId}/deposit-match/available/${product.toUpperCase()}`;
    return useQuery<DepositMatchPromo[]>({
        queryKey: userPromosKeys.depositMatchPromotions(product),
        queryFn: async () => bonusEngineAPIClient.fetch(endpoint).then(resp => resp?.promotions ?? []),
        enabled,
    });
};

/**
 * Activates a deposit match promo. This is done right before the deposit is made to let bonus engine know what promo to match.
 */
export const useDepositMatchPromoActivation = () => {
    const bonusEngineAPIClient = useBonusEngineClient();
    const userId = useUserStore(state => state.profile.sub);
    return useMutation({
        mutationKey: ['activate-deposit-match-promotion'],
        mutationFn: (promoSlug: string) =>
            bonusEngineAPIClient
                .fetch(`/submissions/user/${userId}/promo/${promoSlug}/activate`, {
                    method: 'PATCH',
                })
                .then(() => logger.debug(`Deposit match promo: ${promoSlug} activated`)),
    });
};

/**
 * Hook to retrieve any available deposit match promos and store the promo for which the user is about to deposit
 * @param preferredPromo - The promotion id that the user has just opted into (when redirected from promo page)
 * @param shouldHandleDepositMatch - Flag to determine if deposit match promos should be handled
 *                                  Example: Set to true when user is a first time depositor
 */
export const useDepositMatch = (preferredPromo?: string, shouldHandleDepositMatch?: boolean) => {
    const product = useJurisdictionStore(state => state.product);
    const storeDepositMatchPromo = usePromotionsStore(state => state.actions.storeDepositMatchPromo);
    const clearDepositMatchPromo = usePromotionsStore(state => state.actions.clearDepositMatchPromo);

    const { data: depositMatchPromos, refetch: refetchDepositMatchPromos } = useDepositMatchPromos(product, {
        enabled: false,
    });

    // check if the preferred promo exists in the available deposit match promos
    const isPreferredPromoAvailable = depositMatchPromos?.some(promo => promo.slug === preferredPromo);

    // Select which promo to fetch:
    // if there's a preferred promo and it's available, use it
    // otherwise, use the first available deposit match promo
    const promoIdToFetch = preferredPromo && isPreferredPromoAvailable ? preferredPromo : depositMatchPromos?.[0]?.slug;
    const { data: promoDetails, refetch: refetchPromoDetails } = usePromotion(promoIdToFetch);

    const promotionToActivate =
        promoDetails && promoIdToFetch ? { ...promoDetails, promoSlug: promoIdToFetch } : undefined;

    // Store the deposit match promo so we can activate it when user is depositing
    useEffect(() => {
        if (!shouldHandleDepositMatch) {
            return;
        }
        if (promotionToActivate?.promoSlug) {
            storeDepositMatchPromo(promotionToActivate.promoSlug);
        } else {
            clearDepositMatchPromo();
        }
    }, [clearDepositMatchPromo, promotionToActivate?.promoSlug, shouldHandleDepositMatch, storeDepositMatchPromo]);

    const refetch = useCallback(() => {
        if (shouldHandleDepositMatch) {
            refetchDepositMatchPromos();
            refetchPromoDetails();
        }
    }, [refetchDepositMatchPromos, refetchPromoDetails, shouldHandleDepositMatch]);

    useResumeEffect(refetch);

    return {
        promotionToActivate,
    };
};
